import { useBotUser } from '@/stores/useBotUser';
import Api from '@/common/api';
import type { TransStatement } from '@/common/api/model';
import type { TransactionMetadata } from '@/common/api/models.pleased';
import type { ConnectToAgent } from '@/common/bot/types/config';
import { useConfig } from '@/stores/useConfig';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { analytics, GENERAL_EVENTS } from '@/common/logs';
import { waitFor } from '@/common/utils';
import { isTxStatusPending, TRANSACTION_STATUS_NAME_ENUM, type TransactionStatus } from '@/common/transaction-utils';
import { TransactionType } from '@/common/config';
import {
  checkMetadataRulesForTransaction,
  type UserPlaceholderFields,
  type TransactionRefreshed,
} from '@/common/metadataRule.utils';
import { fetchTransactionMetadata } from './useTransactionMetadata';
import { devLog } from '@/common/debug';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export interface MetadataCheckResult {
  responseText?: string;
  rule?: any;
  transaction: TransStatement & { newStatus?: TransactionStatus };
  transactionMetadata: TransactionMetadata;
}

export interface RefreshTransactionStatusParams {
  transaction: TransStatement;
  transactionType: TransactionType;
  userId: string | number;
  countryCode: string;
  pageSize?: number;
  pageNo?: number;
}

/**
 * Refresh transaction status if it's pending
 * Waits 3 seconds and fetches updated status from the specified page
 */
async function refreshTransactionStatus(
  params: RefreshTransactionStatusParams
): Promise<TransStatement & { newStatus?: TransactionStatus }> {
  const { transaction, transactionType, userId, pageSize = 10, pageNo = 1 } = params;

  const isTxPending = isTxStatusPending(transaction.status);

  if (!isTxPending) {
    return transaction;
  }

  // Wait for 3 seconds before checking status
  await waitFor(3000);

  try {
    // Fetch updated transaction list from the specified page
    const response = await Api.getTransactionHistory({
      type: transactionType,
      pageSize,
      pageNo,
      _t: Date.now(),
    });

    const updatedTransaction = response.data.statements?.find((tx) => tx.tradeId === transaction.tradeId);
    const newStatus = updatedTransaction?.status || transaction.status;

    if (newStatus !== transaction.status) {
      const updatedStatusName = TRANSACTION_STATUS_NAME_ENUM[newStatus].toLowerCase();
      analytics.track({
        name: `metadata_transaction_new_status_${updatedStatusName}` as any,
        metadata: { tradeId: transaction.tradeId },
      });
    }

    return { ...transaction, newStatus };
  } catch (error) {
    logger.error('[METADATA_RULES] Failed to refresh transaction status', {
      error: parseErrorMessage(error),
      userId,
      transactionType,
    });
    return transaction;
  }
}

/**
 * Composable for metadata automation rules
 */
export function useMetadataRules() {
  const { user, displayName } = storeToRefs(useBotUser());
  const { config } = storeToRefs(useConfig());
  const countryCode = computed(() => config.value?.country || '');

  /**
   * Check metadata automation rules for a transaction
   * This includes:
   * 1. Refreshing transaction status if pending
   * 2. Fetching transaction metadata
   * 3. Checking metadata rules against connect config
   */
  const checkMetadataAutomation = async (
    transaction: TransStatement,
    transactionType: TransactionType,
    connectConfig: ConnectToAgent | null,
    options?: {
      userGreyListed?: boolean;
      requestEmail?: string;
      pageSize?: number;
      pageNo?: number;
    }
  ): Promise<MetadataCheckResult | null> => {
    if (!user.value.foreignId) {
      devLog.warn('User ID not available, skipping metadata automation check');
      return null;
    }

    if (!connectConfig?.metadataRules?.length) {
      devLog.warn('No metadata rules configured, skipping automation check');
      return null;
    }

    const { userGreyListed = false, requestEmail, pageSize = 10, pageNo = 1 } = options || {};

    // Step 1: Refresh transaction status if pending
    const refreshedTransaction = await refreshTransactionStatus({
      transaction,
      transactionType,
      userId: user.value.foreignId,
      countryCode: countryCode.value,
      pageSize,
      pageNo,
    });

    // Step 2: Fetch transaction metadata
    const transactionMetadataParams = {
      tradeId: transaction.tradeId,
      userId: user.value.foreignId,
      countryCode: countryCode.value,
    };

    const transactionMetadata = await fetchTransactionMetadata(
      transaction.tradeId,
      user.value.foreignId,
      countryCode.value
    ).catch((err: Error) => {
      analytics.track({
        name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_METADATA_CHECK_EXTERNAL_API_ERROR,
        metadata: { name: err?.name, message: err?.message, err },
      });

      return {
        tradeNumber: null,
        transactionNumber: null,
        sessionId: null,
        isNameConfirmed: null,
      };
    });

    analytics.track({
      name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_METADATA_CHECK_EXTERNAL_API_RESULT,
      selection: transaction.tradeId,
      metadata: {
        transaction,
        params: transactionMetadataParams,
        response: transactionMetadata,
      },
    });

    // Step 3: Check metadata rules
    const userFields: UserPlaceholderFields = {
      email: requestEmail || user.value.email,
      phone: user.value.phone,
      name: displayName.value,
      foreignId: user.value.foreignId,
    };

    const checkMetadataRulesParams = {
      transaction: refreshedTransaction as TransactionRefreshed,
      transactionType,
      connectConfig,
      userGreyListed,
      userFields,
      transactionMetadata,
    };

    const response = checkMetadataRulesForTransaction(checkMetadataRulesParams);

    analytics.track({
      name: GENERAL_EVENTS.CONNECT_REQUEST_TRANSACTION_METADATA_CHECK_RESULT,
      selection: transaction.tradeId,
      metadata: {
        transaction,
        params: checkMetadataRulesParams,
        response: response,
      },
    });

    if (!response) {
      return null;
    }

    return {
      responseText: response.responseText,
      rule: response.rule,
      transaction: refreshedTransaction,
      transactionMetadata,
    };
  };

  return {
    checkMetadataAutomation,
  };
}
